import React, { useEffect, useRef } from 'react';
import { SubmitError } from 'Components/Fields/SubmitError';
import { lifetimeYear, dayOfMonth, required } from 'Utils/FormValidation';
import { SubmitButton } from 'Components/Fields/Buttons';
import { useForm } from 'Hooks/useForm';
import { FormField } from 'Components/HookFormFields/FormField';
import { VETERAN_STATUS } from 'Utils/Constants';
import './SheerIdMilitaryVerificationForm.css';

const STATUS_OPTIONS = [
  {
    value: 'ACTIVE_DUTY',
    label: 'Active Duty',
  },
  {
    value: 'RESERVIST',
    label: 'Reservist',
  },
  {
    value: VETERAN_STATUS,
    label: 'Veteran',
  },
];

const MONTH_OPTIONS = [
  {
    value: '01',
    label: 'January',
  },
  {
    value: '02',
    label: 'February',
  },
  {
    value: '03',
    label: 'March',
  },
  {
    value: '04',
    label: 'April',
  },
  {
    value: '05',
    label: 'May',
  },
  {
    value: '06',
    label: 'June',
  },
  {
    value: '07',
    label: 'July',
  },
  {
    value: '08',
    label: 'August',
  },
  {
    value: '09',
    label: 'September',
  },
  {
    value: '10',
    label: 'October',
  },
  {
    value: '11',
    label: 'November',
  },
  {
    value: '12',
    label: 'December',
  },
];

export const SheerIdMilitaryVerificationForm = ({
  onFormSubmit,
  organizations = [],
  initialValues,
  remount,
  error = '',
}) => {
  const formName = 'sheerIdMilitaryVerificationForm';
  const { formFieldProps, submitting, submitDisabled, formError, handleSubmit } = useForm({
    onFormSubmit,
    formName,
    initialValues,
  });

  const prevShowDischargeDate = useRef();

  const { status } = formFieldProps.fieldValues;
  const showDischargeDate = status === VETERAN_STATUS;

  useEffect(() => {
    //resetting form to clear any info in useForm relating to the discharge date fields
    if (prevShowDischargeDate?.current && !showDischargeDate) {
      remount(formFieldProps.fieldValues);
    } else {
      prevShowDischargeDate.current = showDischargeDate;
    }
  }, [showDischargeDate, formFieldProps.fieldValues, remount]);

  const orgOptions = organizations
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((org) => {
      return { value: org.id.toString(), label: org.name };
    });

  return (
    <form onSubmit={(event) => handleSubmit(event)} className={formName}>
      <section className="formInput">
        <FormField
          name="status"
          label="Status"
          component="SelectField"
          validate={[required]}
          options={STATUS_OPTIONS}
          {...formFieldProps}
        />
        <FormField
          name="organization"
          label="Branch of Service"
          component="SelectField"
          validate={[required]}
          options={orgOptions}
          {...formFieldProps}
        />
        <div className="verificationFormRow">
          <FormField
            name="firstName"
            label="First Name"
            component="InputField"
            validate={[required]}
            autoComplete="given-name"
            {...formFieldProps}
          />
          <FormField
            name="lastName"
            label="Last Name"
            component="InputField"
            validate={[required]}
            autoComplete="family-name"
            {...formFieldProps}
          />
        </div>
        {showDischargeDate && (
          <>
            <div className="verificationRowTitle">Discharge Date</div>
            <div className="verificationFormRow">
              <FormField
                /* name may also be referenced in Page component and css */
                name="dischargeMonth"
                label="Month"
                component="SelectField"
                validate={[required]}
                options={MONTH_OPTIONS}
                {...formFieldProps}
              />
              <FormField
                /* name may also be referenced in Page component and css */
                name="dischargeDay"
                label="Day"
                component="InputField"
                validate={[required, dayOfMonth]}
                {...formFieldProps}
              />
              <FormField
                /* name may also be referenced in Page component and css */
                name="dischargeYear"
                label="Year"
                component="InputField"
                validate={[required, lifetimeYear]}
                {...formFieldProps}
              />
            </div>
          </>
        )}
        <div className="verificationRowTitle">Date of Birth</div>
        <div className="verificationFormRow">
          <FormField
            /* name may also be referenced in Page component and css */
            name="dobMonth"
            label="Month"
            component="SelectField"
            validate={[required]}
            options={MONTH_OPTIONS}
            {...formFieldProps}
          />
          <FormField
            /* name may also be referenced in Page component and css */
            name="dobDay"
            label="Day"
            component="InputField"
            validate={[required, dayOfMonth]}
            {...formFieldProps}
          />
          <FormField
            /* name may also be referenced in Page component and css */
            name="dobYear"
            label="Year"
            component="InputField"
            validate={[required, lifetimeYear]}
            {...formFieldProps}
          />
        </div>
      </section>
      <SubmitError error={error || formError} formName={formName} />
      <SubmitButton text="Continue for 1 Free Year" submitting={submitting} isDisabled={submitDisabled} modernLayout />
    </form>
  );
};
